import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { Text } from '@avanta/shared/components/Text';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { getCenterName, getStatusBadge } from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { type Appointment } from 'types/appointments';
import { formatUnifiedForDisplay } from 'utils/date';
import { SessionTableActions } from '../AppointmentsHistory/SessionTableActions';

interface AssociatedSessionsProps {
  sessions: Appointment[];
}

const getColumns = (): ColumnDef<Appointment>[] => [
  {
    id: 'dateTime',
    accessorKey: 'dateTime',
    enableSorting: true,
    header: (): string => t('AppointmentsTable.date.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string => formatUnifiedForDisplay(row.original.dateTime),
  },
  {
    id: 'psychologist',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.facultative.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string =>
      `${row.original.psychologist.psychologist.name} ${row.original.psychologist.psychologist.surname}`,
  },
  {
    id: 'type',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.type.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string =>
      t(`AppointmentCard.types.${row.original.bookedContactMethod}`, {
        ns: LOCIZE_NS.APPOINTMENTS,
      }),
  },
  {
    id: 'status',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.status.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): React.ReactElement => getStatusBadge(row.original),
  },
  {
    id: 'center',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.center.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string => getCenterName(row.original.bookedContactMethod, row.original.center),
  },
  {
    id: 'actions',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.actions.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): React.ReactElement => <SessionTableActions appointment={row.original} />,
  },
];

export const AssociatedSessions = ({ sessions }: AssociatedSessionsProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  return (
    <div className="pt-3 pb-2">
      <Text
        variant="heading-xs-medium"
        tag="p"
        className="text-denim-800 flex h-10 items-center px-4"
      >
        {t('PatientRecords.associatedSessions.heading', { count: sessions.length })}
      </Text>
      <ClientPaginatedTable
        data={sessions}
        columns={getColumns()}
        emptyMessage={t('PatientRecords.associatedSessions.empty')}
        withoutBorder
      />
    </div>
  );
};
